import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import type { UserRole, AuthUser } from '@/shared/types/auth';

interface AuthContextValue {
  user: AuthUser | null;
  role: UserRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Populated by Phase 2 auth flows via `/api/auth/*`. */
  setUser: (u: AuthUser | null) => void;
  logout: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [isLoading] = useState(false);

  const setUser = useCallback((u: AuthUser | null) => setUserState(u), []);
  const logout = useCallback(() => setUserState(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: user?.role ?? 'guest',
      isAuthenticated: user !== null,
      isLoading,
      setUser,
      logout,
    }),
    [user, isLoading, setUser, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
